/**
 * Dados Mestres: Pessoas, Colaboradores e Cargos (ADR 0039). Validações comuns à API e às telas,
 * situação funcional, completude cadastral e rateios. Sem cálculo de folha, encargos ou salários.
 */
import { z } from 'zod';
import { SALARY_STEPS } from './masterdata-import.ts';
import { toScaled } from './money.ts';
import { isValidCpf } from './partners.ts';
import { normalizePhone } from './phone.ts';
import { localizedText, uuid } from './schemas.ts';

const trimmed = (max: number) => z.string().trim().min(1).max(max);
const optionalText = (max: number, min = 1) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .refine((v) => v === null || v.length >= min, `Mínimo de ${min} caracteres`)
    .nullable()
    .optional();
const optionalDate = z.iso
  .date()
  .or(z.literal('').transform(() => null))
  .nullable()
  .optional();
const optionalUuid = uuid
  .or(z.literal('').transform(() => null))
  .nullable()
  .optional();
const email = z
  .string()
  .trim()
  .toLowerCase()
  .max(254)
  .regex(/^[^@\s]+@[^@\s]+\.[^@\s]+$/, 'E-mail inválido');
const optionalEmail = email
  .or(z.literal('').transform(() => null))
  .nullable()
  .optional();
const phone = z
  .string()
  .trim()
  .regex(/^\+?[0-9 ()-]{8,25}$/, 'Telefone inválido (só números, espaço, parênteses e hífen)')
  .transform(normalizePhone);
const optionalPhone = phone
  .or(z.literal('').transform(() => null))
  .nullable()
  .optional();
const country = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{2}$/, 'Use o código do país com 2 letras (ex.: BR)');
const code = (max: number) =>
  z
    .string()
    .trim()
    .toUpperCase()
    .regex(
      new RegExp(`^[A-Z0-9][A-Z0-9._-]{0,${max - 1}}$`),
      'Use letras, números, ponto, hífen ou _',
    );

// ----- Listas -----

export const PERSON_STATUSES = ['active', 'inactive'] as const;
export const MARITAL_STATUSES = [
  'single',
  'married',
  'stable_union',
  'separated',
  'divorced',
  'widowed',
  'other',
] as const;
export const FOREIGN_ID_TYPES = ['passport', 'rnm', 'other'] as const;
export const WORK_MODES = ['onsite', 'hybrid', 'remote'] as const;
export type WorkMode = (typeof WORK_MODES)[number];
/** Situação do vínculo no histórico. Afastado e em licença vêm das ocorrências (não encerram). */
export const EMPLOYMENT_STATUSES = ['active', 'terminated', 'ended'] as const;
export type EmploymentStatus = (typeof EMPLOYMENT_STATUSES)[number];
export const LEAVE_KINDS = ['leave', 'absence'] as const;
export type LeaveKind = (typeof LEAVE_KINDS)[number];
/** Situação funcional que as telas mostram (vínculo + afastamento ou licença vigente). */
/**
 * Situação funcional que as telas mostram (vínculo + afastamento ou licença vigente). Sem nenhuma
 * movimentação na data, é "não informada": não se presume ativo nem desligado.
 */
export const FUNCTIONAL_STATUSES = [
  'not_informed',
  'active',
  'absent',
  'on_leave',
  'terminated',
  'ended',
] as const;
export type FunctionalStatus = (typeof FUNCTIONAL_STATUSES)[number];
export const SUBFAMILY_KINDS = ['structural', 'structural_track', 'practice'] as const;
/** Situação cadastral (o preenchimento do cadastro), separada da situação funcional. */
export const REGISTRATION_STATUSES = ['in_progress', 'complete', 'inactive'] as const;
export type RegistrationStatus = (typeof REGISTRATION_STATUSES)[number];
/** Categorias administrativas de afastamento e licença (sem dados de saúde). */
export const LEAVE_CATEGORIES = [
  'maternity',
  'paternity',
  'adoption',
  'social_security',
  'unpaid',
  'legal',
  'other',
] as const;
export type LeaveCategory = (typeof LEAVE_CATEGORIES)[number];

/** Campos do histórico funcional (cada movimentação muda um ou mais). */
export const MOVEMENT_FIELDS = [
  'legalEntityId',
  'branchId',
  'departmentId',
  'jobId',
  'subfamilyId',
  'managerEmployeeId',
  'isTopLevel',
  'costCenterId',
  'locationId',
  'workMode',
  'status',
] as const;
export type MovementField = (typeof MOVEMENT_FIELDS)[number];

/**
 * Listas da situação funcional (ADR 0054 e 0096): subfamílias de atuação além da principal, famílias
 * de atuação inteira, famílias e subfamílias lideradas e departamentos adicionais. Não informada na
 * movimentação = a da situação vigente, levada adiante. Nenhuma dá alçada nem muda o gestor imediato.
 */
export const MOVEMENT_LIST_FIELDS = [
  'practiceSubfamilyIds',
  'practiceFamilyIds',
  'ledFamilyIds',
  'ledSubfamilyIds',
  'practiceDepartmentIds',
  'ledDepartmentIds',
] as const;
export type MovementListField = (typeof MOVEMENT_LIST_FIELDS)[number];
/** Até quantos itens cada lista aceita (o banco confere o mesmo limite). */
export const MOVEMENT_LIST_MAX = 50;
/** Liderança numa família: nenhuma, a família inteira ou só algumas subfamílias dela. */
export const FAMILY_LEADERSHIPS = ['none', 'family', 'subfamilies'] as const;
export type FamilyLeadership = (typeof FAMILY_LEADERSHIPS)[number];
/** Abrangência da atuação numa família. `all` é uma regra: vale para as subfamílias ativas dela. */
export const FAMILY_SCOPES = ['all', 'some'] as const;
export type FamilyScope = (typeof FAMILY_SCOPES)[number];

// ----- CPF -----

/** Só os números do CPF, ou null se vazio. */
export function cpfDigits(value: string | null | undefined): string | null {
  const d = (value ?? '').replace(/\D/g, '');
  return d === '' ? null : d;
}

export function formatCpf(digits: string): string {
  return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
}

const cpf = z
  .string()
  .trim()
  .transform((v) => cpfDigits(v))
  .refine((v) => v === null || isValidCpf(v), 'CPF inválido')
  .nullable()
  .optional();

// ----- Entradas -----

/** Cadastro básico da pessoa (sem dados restritos). Só o nome é obrigatório. */
export const personInput = z.object({
  fullName: z.string().trim().min(2, 'Informe o nome completo').max(200),
  socialName: optionalText(200, 2),
  displayName: optionalText(120),
  corporateEmail: optionalEmail,
  status: z.enum(PERSON_STATUSES).default('active'),
  userId: optionalUuid,
  notes: optionalText(2000),
  /** Confirma que é outra pessoa, mesmo havendo nome igual ou parecido (nada é unido sozinho). */
  confirmNewPerson: z.boolean().optional(),
});
export type PersonInput = z.infer<typeof personInput>;

/** Dados pessoais restritos. CPF vazio mantém o guardado; `clearCpf` apaga. */
export const personPrivateInput = z
  .object({
    cpf,
    clearCpf: z.boolean().default(false),
    birthDate: optionalDate.refine(
      (v) => !v || (v >= '1900-01-01' && v <= new Date().toISOString().slice(0, 10)),
      'Data de nascimento inválida',
    ),
    nationality: country
      .or(z.literal('').transform(() => null))
      .nullable()
      .optional(),
    foreignIdType: z
      .enum(FOREIGN_ID_TYPES)
      .or(z.literal('').transform(() => null))
      .nullable()
      .optional(),
    foreignIdNumber: optionalText(40),
    maritalStatus: z
      .enum(MARITAL_STATUSES)
      .or(z.literal('').transform(() => null))
      .nullable()
      .optional(),
    personalEmail: optionalEmail,
    mobilePhone: optionalPhone,
    altPhone: optionalPhone,
    postalCode: z
      .string()
      .trim()
      .regex(/^[A-Za-z0-9 -]{3,12}$/, 'CEP inválido')
      .or(z.literal('').transform(() => null))
      .nullable()
      .optional(),
    /** Não aparecer nos aniversariantes (ADR 0069). Ausente, mantém o que está gravado. */
    hideBirthday: z.boolean().optional(),
    street: optionalText(200),
    streetNumber: optionalText(20),
    complement: optionalText(100),
    district: optionalText(100),
    city: optionalText(100),
    state: optionalText(50),
    country: country
      .or(z.literal('').transform(() => null))
      .nullable()
      .optional(),
  })
  .refine((v) => !v.foreignIdType === !v.foreignIdNumber, {
    message: 'Informe o tipo e o número do documento estrangeiro juntos',
    path: ['foreignIdNumber'],
  })
  .refine(
    (v) => !v.postalCode || (v.country ?? 'BR') !== 'BR' || /^\d{5}-?\d{3}$/.test(v.postalCode),
    { message: 'CEP do Brasil: 8 números (00000-000)', path: ['postalCode'] },
  );
export type PersonPrivateInput = z.infer<typeof personPrivateInput>;

/** Procura de pessoas parecidas antes de cadastrar (nome, e-mail ou CPF). */
export const personDuplicateInput = z.object({
  fullName: z.string().trim().max(200).optional(),
  corporateEmail: z.string().trim().max(254).optional(),
  cpf: z.string().trim().max(20).optional(),
  excludeId: optionalUuid,
});

export const employmentTypeInput = z.object({
  code: code(30),
  name: localizedText,
  hasRegistration: z.boolean().default(true),
  hasWorkday: z.boolean().default(true),
  isActive: z.boolean().default(true),
  sortOrder: z.number().int().min(0).max(999).default(0),
});
export type EmploymentTypeInput = z.infer<typeof employmentTypeInput>;

/** Dados do vínculo (identificação funcional e informações trabalhistas básicas). */
export const employeeInput = z
  .object({
    registrationNumber: z
      .string()
      .trim()
      .regex(/^[A-Za-z0-9._/-]{1,30}$/, 'Matrícula: letras, números, ponto, barra ou hífen')
      .or(z.literal('').transform(() => null))
      .nullable()
      .optional(),
    externalCode: optionalText(60),
    legalEntityId: optionalUuid,
    employmentTypeId: optionalUuid,
    startDate: optionalDate,
    endDate: optionalDate,
    workday: optionalText(200),
    weeklyHours: z
      .union([z.number(), z.string().trim()])
      .transform((v) => (v === '' ? null : Number(v)))
      .refine((v) => v === null || (Number.isFinite(v) && v > 0 && v <= 168), 'Entre 0 e 168 horas')
      .nullable()
      .optional(),
    unionName: optionalText(200),
    collectiveAgreement: optionalText(300),
    notes: optionalText(2000),
  })
  .refine((v) => !v.endDate || !v.startDate || v.endDate >= v.startDate, {
    message: 'O término não pode ser antes do início',
    path: ['endDate'],
  });
export type EmployeeInput = z.infer<typeof employeeInput>;

/** Situação organizacional numa movimentação (campos vazios ficam sem valor). */
/**
 * Um bloco da tela de movimentação: uma família com a atuação e a liderança dentro dela. Cada
 * subfamília tem de ser da família do bloco (a API confere pelo cadastro, nunca pelo nome). Na
 * gravação, os blocos viram as listas da situação funcional (ADR 0054).
 */
export const movementFamily = z
  .object({
    familyId: uuid,
    scope: z.enum(FAMILY_SCOPES).default('some'),
    /** Com `scope` igual a `some`: subfamílias de atuação, inclusive a principal, se for dela. */
    practiceSubfamilyIds: z.array(uuid).max(MOVEMENT_LIST_MAX).default([]),
    leadership: z.enum(FAMILY_LEADERSHIPS).default('none'),
    /** Só com `leadership` igual a `subfamilies`. */
    ledSubfamilyIds: z.array(uuid).max(MOVEMENT_LIST_MAX).default([]),
  })
  .refine((v) => v.leadership !== 'subfamilies' || v.ledSubfamilyIds.length > 0, {
    message: 'Marque as subfamílias lideradas',
    path: ['ledSubfamilyIds'],
  })
  .refine((v) => v.leadership === 'subfamilies' || v.ledSubfamilyIds.length === 0, {
    message: 'Subfamílias lideradas só com a opção de liderar subfamílias',
    path: ['ledSubfamilyIds'],
  })
  .refine((v) => v.scope === 'some' || v.practiceSubfamilyIds.length === 0, {
    message: 'Em toda a família não se marcam subfamílias',
    path: ['practiceSubfamilyIds'],
  })
  .refine(
    (v) => v.scope === 'all' || v.practiceSubfamilyIds.length > 0 || v.leadership !== 'none',
    {
      message: 'Marque ao menos uma subfamília ou a liderança, ou remova a família',
      path: ['practiceSubfamilyIds'],
    },
  );
export type FamilyBlock = z.infer<typeof movementFamily>;

export const movementInput = z
  .object({
    validFrom: z.iso.date(),
    legalEntityId: optionalUuid,
    branchId: optionalUuid,
    departmentId: optionalUuid,
    jobId: optionalUuid,
    /** Subfamília em que a pessoa de fato atua: a principal do cargo ou uma das de atuação. */
    subfamilyId: optionalUuid,
    /** Outras subfamílias de atuação (ADR 0054); ausente = as da situação vigente. */
    practiceSubfamilyIds: z.array(uuid).max(MOVEMENT_LIST_MAX).optional(),
    /** Famílias de atuação inteira (só cargos da trilha Liderança); ausente = as vigentes. */
    practiceFamilyIds: z.array(uuid).max(MOVEMENT_LIST_MAX).optional(),
    /** Famílias lideradas inteiras (só cargos da trilha Liderança); ausente = as vigentes. */
    ledFamilyIds: z.array(uuid).max(MOVEMENT_LIST_MAX).optional(),
    /** Subfamílias lideradas (só cargos da trilha Liderança); ausente = as vigentes. */
    ledSubfamilyIds: z.array(uuid).max(MOVEMENT_LIST_MAX).optional(),
    /** Atuação e liderança por família, no lugar das três listas acima (a tela usa só esta). */
    families: z.array(movementFamily).max(MOVEMENT_LIST_MAX).optional(),
    /** Departamentos em que também atua, além do principal (ADR 0096); ausente = os vigentes. */
    practiceDepartmentIds: z.array(uuid).max(MOVEMENT_LIST_MAX).optional(),
    /** Departamentos que lidera (só cargos da trilha Liderança); ausente = os vigentes. */
    ledDepartmentIds: z.array(uuid).max(MOVEMENT_LIST_MAX).optional(),
    managerEmployeeId: optionalUuid,
    isTopLevel: z.boolean().default(false),
    costCenterId: optionalUuid,
    locationId: optionalUuid,
    workMode: z
      .enum(WORK_MODES)
      .or(z.literal('').transform(() => null))
      .nullable()
      .optional(),
    status: z.enum(EMPLOYMENT_STATUSES).default('active'),
    reason: optionalText(1000),
  })
  .refine((v) => !(v.isTopLevel && v.managerEmployeeId), {
    message: 'Quem está no topo da estrutura não tem gestor imediato',
    path: ['managerEmployeeId'],
  })
  .refine((v) => !v.subfamilyId || !!v.jobId, {
    message: 'Informe o cargo para registrar a subfamília de atuação',
    path: ['subfamilyId'],
  })
  .refine((v) => !v.practiceSubfamilyIds?.length || !!v.subfamilyId, {
    message: 'Informe a subfamília de atuação principal antes das demais',
    path: ['practiceSubfamilyIds'],
  })
  .refine((v) => !v.practiceFamilyIds?.length || !!v.subfamilyId, {
    message: 'Informe a subfamília de atuação principal antes das demais',
    path: ['practiceFamilyIds'],
  })
  .refine((v) => !v.ledDepartmentIds?.length || !!v.jobId, {
    message: 'Informe o cargo para registrar a liderança',
    path: ['ledDepartmentIds'],
  })
  .refine((v) => !(v.ledFamilyIds?.length || v.ledSubfamilyIds?.length) || !!v.jobId, {
    message: 'Informe o cargo para registrar a liderança',
    path: ['ledFamilyIds'],
  })
  .refine(
    (v) =>
      !v.families ||
      !(v.practiceSubfamilyIds || v.practiceFamilyIds || v.ledFamilyIds || v.ledSubfamilyIds),
    {
      message: 'Informe a atuação por família ou pelas listas, não pelos dois',
      path: ['families'],
    },
  )
  .refine((v) => new Set(v.families?.map((f) => f.familyId)).size === (v.families?.length ?? 0), {
    message: 'A mesma família não pode aparecer duas vezes',
    path: ['families'],
  })
  .refine(
    (v) =>
      !v.families?.some((f) => f.scope === 'all' || f.practiceSubfamilyIds.length > 0) ||
      !!v.subfamilyId,
    { message: 'Informe a subfamília de atuação principal', path: ['subfamilyId'] },
  )
  .refine((v) => !v.families?.some((f) => f.leadership !== 'none') || !!v.jobId, {
    message: 'Informe o cargo para registrar a liderança',
    path: ['families'],
  });
export type MovementInput = z.infer<typeof movementInput>;

/** A situação gravada vista por família: um bloco por família com atuação ou liderança. */
export function familyBlocksOf(
  state: {
    subfamilyId: string | null;
    practiceSubfamilyIds: readonly string[];
    practiceFamilyIds: readonly string[];
    ledFamilyIds: readonly string[];
    ledSubfamilyIds: readonly string[];
  },
  subfamilies: readonly { id: string; familyId: string }[],
): FamilyBlock[] {
  const familyOf = new Map(subfamilies.map((s) => [s.id, s.familyId]));
  const whole = new Set(state.practiceFamilyIds);
  const blocks = new Map<string, FamilyBlock>();
  const blockOf = (familyId: string) => {
    const block = blocks.get(familyId) ?? {
      familyId,
      scope: whole.has(familyId) ? ('all' as const) : ('some' as const),
      practiceSubfamilyIds: [],
      leadership: 'none' as const,
      ledSubfamilyIds: [],
    };
    blocks.set(familyId, block);
    return block;
  };
  for (const id of new Set([state.subfamilyId, ...state.practiceSubfamilyIds])) {
    const familyId = id && familyOf.get(id);
    if (!familyId) continue;
    const block = blockOf(familyId);
    if (block.scope === 'some') block.practiceSubfamilyIds.push(id);
  }
  for (const familyId of whole) blockOf(familyId);
  for (const familyId of state.ledFamilyIds) blockOf(familyId).leadership = 'family';
  for (const id of state.ledSubfamilyIds) {
    const familyId = familyOf.get(id);
    if (!familyId) continue;
    const block = blockOf(familyId);
    if (block.leadership === 'family') continue;
    block.leadership = 'subfamilies';
    block.ledSubfamilyIds.push(id);
  }
  return [...blocks.values()];
}

/** As listas da situação funcional a partir dos blocos. A principal fica fora de "também atua". */
export function familyLists(
  blocks: readonly FamilyBlock[],
  subfamilyId: string | null | undefined,
) {
  return {
    practiceSubfamilyIds: [...new Set(blocks.flatMap((b) => b.practiceSubfamilyIds))].filter(
      (id) => id !== subfamilyId,
    ),
    practiceFamilyIds: blocks.filter((b) => b.scope === 'all').map((b) => b.familyId),
    ledFamilyIds: blocks.filter((b) => b.leadership === 'family').map((b) => b.familyId),
    ledSubfamilyIds: blocks.flatMap((b) =>
      b.leadership === 'subfamilies' ? b.ledSubfamilyIds : [],
    ),
  };
}

/** Novo colaborador: a pessoa (existente ou nova, só com o nome) e o que já se sabe do vínculo. */
export const newEmployeeInput = z
  .object({
    personId: optionalUuid,
    newPersonName: optionalText(200, 2),
    /** Pessoa nova com nome igual ou parecido com alguém cadastrado: confirmação de que é outra. */
    confirmNewPerson: z.boolean().optional(),
    employee: employeeInput.default({}),
    /** Situação inicial (opcional): grava a primeira linha do histórico. */
    initial: movementInput.nullable().optional(),
  })
  .refine((v) => !!v.personId !== !!v.newPersonName, {
    message: 'Escolha uma pessoa cadastrada ou informe o nome de uma nova',
    path: ['personId'],
  });
export type NewEmployeeInput = z.infer<typeof newEmployeeInput>;

export const leaveInput = z
  .object({
    kind: z.enum(LEAVE_KINDS),
    category: z.enum(LEAVE_CATEGORIES).default('other'),
    /** Detalhe administrativo (restrito ao DP; nunca dados de saúde). */
    detail: optionalText(500),
    startDate: z.iso.date(),
    endDate: optionalDate,
  })
  .refine((v) => !v.endDate || v.endDate >= v.startDate, {
    message: 'O término não pode ser antes do início',
    path: ['endDate'],
  });
export type LeaveInput = z.infer<typeof leaveInput>;

/** Detalhe administrativo do afastamento (restrito a quem cuida dos dados pessoais). */
export const leaveDetailInput = z.object({ detail: z.string().trim().min(1).max(500) });

export const leaveEndInput = z.object({
  endDate: optionalDate,
  cancel: z.boolean().default(false),
});

/** Percentual com até 4 casas, em texto (nunca ponto flutuante na regra). */
const percentage = z
  .union([z.number(), z.string().trim()])
  .transform((v) => String(v).replace(',', '.'))
  .refine((v) => /^\d{1,3}(\.\d{1,4})?$/.test(v) && Number(v) > 0 && Number(v) <= 100, {
    message: 'Percentual entre 0 e 100, com até 4 casas',
  });

/** Soma percentuais em décimos de milésimo (inteiros), para comparar com 100 sem erro de arredondamento. */
export function percentageTotal(values: string[]): number {
  return values.reduce((sum, v) => {
    const [int, dec = ''] = v.split('.');
    return sum + Number(int) * 10000 + Number(dec.padEnd(4, '0'));
  }, 0);
}
export const HUNDRED_PERCENT = 1_000_000;

export const allocationInput = z
  .object({
    validFrom: z.iso.date(),
    /** Término (opcional): obrigatório quando um centro de custo da composição tem fim de vigência. */
    validUntil: z.iso.date().nullable().optional(),
    notes: optionalText(1000),
    lines: z
      .array(
        z.object({
          costCenterId: uuid,
          percentage,
          notes: optionalText(500),
        }),
      )
      .min(1, 'Informe pelo menos um centro de custo')
      .max(30),
  })
  .refine((v) => new Set(v.lines.map((l) => l.costCenterId)).size === v.lines.length, {
    message: 'Cada centro de custo aparece uma vez só',
    path: ['lines'],
  })
  .refine((v) => !v.validUntil || v.validUntil >= v.validFrom, {
    message: 'O término do rateio é anterior ao início',
    path: ['validUntil'],
  })
  .refine((v) => percentageTotal(v.lines.map((l) => l.percentage)) === HUNDRED_PERCENT, {
    message: 'Os percentuais precisam somar 100%',
    path: ['lines'],
  });
export type AllocationInput = z.infer<typeof allocationInput>;

/** Competência AAAA-MM: primeiro e último dia do mês. */
export function competenceRange(competence: string): { from: string; to: string } | null {
  const m = /^(\d{4})-(\d{2})$/.exec(competence);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  if (month < 1 || month > 12) return null;
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return { from: `${m[1]}-${m[2]}-01`, to: `${m[1]}-${m[2]}-${String(last).padStart(2, '0')}` };
}

// ----- Catálogo de cargos -----

export const jobFamilyInput = z.object({
  code: code(20),
  name: trimmed(120),
  shortCode: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9]{2,10}$/, 'Prefixo de 2 a 10 letras ou números (ex.: BKO)')
    .or(z.literal('').transform(() => null))
    .nullable()
    .optional(),
  description: optionalText(2000),
  isActive: z.boolean().default(true),
});
export type JobFamilyInput = z.infer<typeof jobFamilyInput>;

export const jobSubfamilyInput = z.object({
  familyId: uuid,
  code: code(20),
  name: trimmed(120),
  kind: z
    .enum(SUBFAMILY_KINDS)
    .or(z.literal('').transform(() => null))
    .nullable()
    .optional(),
  description: optionalText(2000),
  isActive: z.boolean().default(true),
});
export type JobSubfamilyInput = z.infer<typeof jobSubfamilyInput>;

export const jobLevelInput = z.object({
  code: code(20),
  name: trimmed(120),
  rank: z.coerce.number().int().min(0).max(999),
  description: optionalText(2000),
  isActive: z.boolean().default(true),
});
export type JobLevelInput = z.infer<typeof jobLevelInput>;

export const careerTrackInput = z.object({
  code: code(20),
  name: trimmed(120),
  description: optionalText(2000),
  isActive: z.boolean().default(true),
});
export type CareerTrackInput = z.infer<typeof careerTrackInput>;

export const salaryGradeInput = z.object({
  code: code(30),
  familyId: optionalUuid,
  levelId: optionalUuid,
  description: optionalText(2000),
  isActive: z.boolean().default(true),
});
export type SalaryGradeInput = z.infer<typeof salaryGradeInput>;

const stepAmount = z
  .union([z.number(), z.string().trim()])
  .transform((v) => String(v).replace(',', '.'))
  .refine((v) => /^\d{1,15}(\.\d{1,4})?$/.test(v) && Number(v) > 0, 'Valor positivo, até 4 casas');
const currencyCode = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{3}$/, 'Moeda com 3 letras');

export const salaryStepInput = z
  .object({
    step: z.coerce.number().int().min(-10).max(20),
    validFrom: z.iso.date(),
    validUntil: optionalDate,
    amount: stepAmount,
    currency: currencyCode,
  })
  .refine((v) => !v.validUntil || v.validUntil >= v.validFrom, {
    message: 'O fim da vigência não pode ser antes do início',
    path: ['validUntil'],
  });
export type SalaryStepInput = z.infer<typeof salaryStepInput>;

/** Os sete steps da grade, na ordem da matriz (Step 0 = midpoint). */
export const GRADE_STEPS = SALARY_STEPS;
export const stepLabel = (step: number): string => (step > 0 ? `+${step}` : String(step));

/**
 * Nova vigência dos sete steps de uma grade, gravada de uma vez. A ordem entre os steps depende do
 * nível da grade (`stepOrderIssues`) e é conferida por quem grava. Nada é calculado: os sete
 * valores vêm de quem preenche.
 */
export const salaryGradeValuesInput = z.object({
  validFrom: z.iso.date(),
  currency: currencyCode,
  amounts: z.array(stepAmount).length(GRADE_STEPS.length, 'Informe os sete steps'),
});
export type SalaryGradeValuesInput = z.infer<typeof salaryGradeValuesInput>;

/** Níveis com faixa única: os sete steps podem ter o mesmo valor (estágio). */
export const FLAT_GRADE_LEVELS: readonly string[] = ['ETG'];
export const isFlatGradeLevel = (levelCode: string | null | undefined): boolean =>
  !!levelCode && FLAT_GRADE_LEVELS.includes(levelCode.trim().toUpperCase());

const VALID_AMOUNT = /^\d{1,15}(\.\d{1,4})?$/;

/**
 * Ordem dos steps preenchidos (na ordem -2 … +4), a mesma na janela, no servidor e no importador:
 * cada valor maior que o do step anterior; na faixa única (estágio), igual também vale, mas nunca
 * menor. Valores inválidos ficam de fora (já têm o erro do próprio campo). A mensagem não repete
 * valores.
 */
export function stepOrderIssues(
  entries: readonly { step: number; amount: string | null | undefined }[],
  flat: boolean,
): { step: number; previous: number; message: string }[] {
  const issues: { step: number; previous: number; message: string }[] = [];
  let previous: { step: number; amount: string } | null = null;
  for (const { step, amount } of entries) {
    if (!amount || !VALID_AMOUNT.test(amount)) continue;
    if (previous) {
      const [cur, prev] = [toScaled(amount), toScaled(previous.amount)];
      if (flat ? cur < prev : cur <= prev) {
        const label = stepLabel(previous.step);
        issues.push({
          step,
          previous: previous.step,
          message: flat
            ? `Não pode ser menor que o Step ${label}`
            : `Precisa ser maior que o Step ${label}`,
        });
      }
    }
    previous = { step, amount };
  }
  return issues;
}

/**
 * Valor digitado no padrão brasileiro ("4.920", "4.920,50", "R$ 4.920") em texto decimal
 * ("4920", "4920.50"). Texto que não for número volta como veio, para a validação recusar.
 */
export function parseAmountInput(raw: string): string {
  const v = raw.replace(/R\$|\s/g, '');
  if (v.includes(',')) return v.replace(/\./g, '').replace(',', '.');
  if (/^\d{1,3}(\.\d{3})+$/.test(v)) return v.replace(/\./g, '');
  return v;
}

export const jobInput = z
  .object({
    code: code(20),
    functionCode: code(20)
      .or(z.literal('').transform(() => null))
      .nullable()
      .optional(),
    name: z.string().trim().min(2).max(200),
    shortName: optionalText(30),
    marketName: optionalText(200, 2),
    description: optionalText(4000),
    familyId: optionalUuid,
    subfamilyId: optionalUuid,
    levelId: optionalUuid,
    trackId: optionalUuid,
    cbo: z
      .string()
      .trim()
      .regex(/^\d{6}$/, 'CBO: 6 números')
      .or(z.literal('').transform(() => null))
      .nullable()
      .optional(),
    gradeId: optionalUuid,
    validFrom: optionalDate,
    validUntil: optionalDate,
    notes: optionalText(2000),
    /** Subfamílias de atuação (além da principal), com o nome mercadológico de cada uma. */
    practices: z
      .array(z.object({ subfamilyId: uuid, marketName: optionalText(200, 2) }))
      .max(30)
      .default([]),
  })
  .refine((v) => !v.validUntil || !v.validFrom || v.validUntil >= v.validFrom, {
    message: 'O fim da vigência não pode ser antes do início',
    path: ['validUntil'],
  })
  .refine((v) => new Set(v.practices.map((p) => p.subfamilyId)).size === v.practices.length, {
    message: 'Cada subfamília de atuação aparece uma vez só',
    path: ['practices'],
  });
export type JobInput = z.infer<typeof jobInput>;

export const catalogStatusInput = z.object({ isActive: z.boolean() });

// ----- Situação funcional -----

/** Situação que as telas mostram: desligado/encerrado pelo histórico; senão, a ocorrência vigente. */
export function functionalStatus(
  status: EmploymentStatus | null | undefined,
  leaves: { kind: LeaveKind; startDate: string; endDate: string | null; status: string }[],
  on: string,
): FunctionalStatus {
  if (!status) return 'not_informed';
  if (status !== 'active') return status;
  const current = leaves.find(
    (l) => l.status === 'active' && l.startDate <= on && (!l.endDate || l.endDate >= on),
  );
  if (!current) return 'active';
  return current.kind === 'leave' ? 'on_leave' : 'absent';
}

/** Situação cadastral: inativo pela pessoa; completo pela completude; senão, em preenchimento. */
export function registrationStatus(
  personStatus: string | null | undefined,
  c: { level: 'complete' | 'partial' | 'pending' },
): RegistrationStatus {
  if (personStatus === 'inactive') return 'inactive';
  return c.level === 'complete' ? 'complete' : 'in_progress';
}

/**
 * Filtro da lista de colaboradores pela situação funcional. O padrão ("current") mostra todos os
 * que não foram desligados nem encerrados, inclusive os cadastros ainda sem movimentação.
 */
export const EMPLOYEE_STATUS_FILTERS = [
  'current',
  'not_informed',
  'active',
  'away',
  'terminated',
  'all',
] as const;
export type EmployeeStatusFilter = (typeof EMPLOYEE_STATUS_FILTERS)[number];

export function matchesStatusFilter(
  status: FunctionalStatus,
  filter: EmployeeStatusFilter,
): boolean {
  switch (filter) {
    case 'current':
      return status !== 'terminated' && status !== 'ended';
    case 'not_informed':
      return status === 'not_informed';
    case 'active':
      return status === 'active';
    case 'away':
      return status === 'absent' || status === 'on_leave';
    case 'terminated':
      return status === 'terminated' || status === 'ended';
    default:
      return true;
  }
}

// ----- Completude cadastral -----

/**
 * Regras de completude: cada item diz a que se aplica. Evoluem aqui, sem mexer no banco nem nas
 * telas (ex.: novos tipos de vínculo). Nunca impedem o uso do cadastro e nunca inventam valores.
 */
export interface CompletenessContext {
  /** Tem vínculo (colaborador) ou é só pessoa. */
  hasEmployment: boolean;
  /** Matrícula e jornada se aplicam ao tipo de vínculo? (desconhecido = sim) */
  hasRegistration: boolean;
  hasWorkday: boolean;
  /** Pessoa estrangeira (o documento é o estrangeiro, não o CPF). */
  foreign: boolean;
  /**
   * O cargo vigente não tem subfamília principal (cargo transversal): a subfamília de atuação da
   * pessoa passa a contar (ADR 0044). Nos demais cargos, vale a principal do cargo.
   */
  transversalJob?: boolean;
}

export interface CompletenessFacts {
  /** Campos preenchidos (chaves abaixo). */
  filled: ReadonlySet<string>;
}

export const COMPLETENESS_RULES: readonly {
  key: string;
  group: 'personal' | 'employment' | 'organization';
  applies: (c: CompletenessContext) => boolean;
}[] = [
  { key: 'document', group: 'personal', applies: () => true },
  { key: 'birth_date', group: 'personal', applies: () => true },
  { key: 'nationality', group: 'personal', applies: (c) => c.foreign },
  { key: 'personal_email', group: 'personal', applies: () => true },
  { key: 'mobile_phone', group: 'personal', applies: () => true },
  { key: 'address', group: 'personal', applies: () => true },
  { key: 'corporate_email', group: 'personal', applies: (c) => c.hasEmployment },
  { key: 'legal_entity', group: 'employment', applies: (c) => c.hasEmployment },
  { key: 'employment_type', group: 'employment', applies: (c) => c.hasEmployment },
  {
    key: 'registration_number',
    group: 'employment',
    applies: (c) => c.hasEmployment && c.hasRegistration,
  },
  { key: 'start_date', group: 'employment', applies: (c) => c.hasEmployment },
  { key: 'workday', group: 'employment', applies: (c) => c.hasEmployment && c.hasWorkday },
  { key: 'job', group: 'organization', applies: (c) => c.hasEmployment },
  {
    key: 'subfamily',
    group: 'organization',
    applies: (c) => c.hasEmployment && !!c.transversalJob,
  },
  { key: 'department', group: 'organization', applies: (c) => c.hasEmployment },
  { key: 'cost_center', group: 'organization', applies: (c) => c.hasEmployment },
  { key: 'manager', group: 'organization', applies: (c) => c.hasEmployment },
  { key: 'work_mode', group: 'organization', applies: (c) => c.hasEmployment },
];

export type CompletenessLevel = 'complete' | 'partial' | 'pending';

export interface Completeness {
  level: CompletenessLevel;
  percent: number;
  /** Chaves aplicáveis ainda sem valor (sem expor nenhum valor). */
  missing: string[];
}

/** Completo = 100%; parcial = pelo menos metade; pendente = menos da metade. */
export function completeness(context: CompletenessContext, facts: CompletenessFacts): Completeness {
  const applicable = COMPLETENESS_RULES.filter((r) => r.applies(context));
  const missing = applicable.filter((r) => !facts.filled.has(r.key)).map((r) => r.key);
  const done = applicable.length - missing.length;
  const percent = applicable.length === 0 ? 100 : Math.round((done / applicable.length) * 100);
  const level: CompletenessLevel =
    missing.length === 0 ? 'complete' : percent >= 50 ? 'partial' : 'pending';
  return { level, percent, missing };
}

/** Nome para exibir: o preferido, senão o social, senão o completo. */
export function personDisplayName(p: {
  fullName: string;
  socialName?: string | null;
  displayName?: string | null;
}): string {
  return p.displayName || p.socialName || p.fullName;
}

/** Aniversariantes de um mês (1 a 12); sem o mês, todos. */
export const birthdayQuery = z.object({ month: z.coerce.number().int().min(1).max(12).optional() });

/** Lista de envio de lembranças em planilha: filtros da tela e colunas já traduzidas. */
export const mailingExportInput = z.object({
  legalEntityId: z.uuid().nullable().optional(),
  branchId: z.uuid().nullable().optional(),
  columns: z
    .array(z.object({ key: z.string().regex(/^[a-zA-Z0-9_]{1,40}$/), label: z.string().max(80) }))
    .min(1)
    .max(20),
});

/** Tamanhos e restrições alimentares (ADR 0078), para brindes e eventos. */
export const SHIRT_SIZES = ['PP', 'P', 'M', 'G', 'GG', 'XG'] as const;
export const SHOE_SIZE_MIN = 33;
export const SHOE_SIZE_MAX = 46;
export const DIETARY_RESTRICTIONS = [
  'lactose',
  'gluten',
  'seafood',
  'nuts',
  'egg',
  'vegetarian',
  'vegan',
] as const;
export type DietaryRestriction = (typeof DIETARY_RESTRICTIONS)[number];

/**
 * Restrição alimentar é informação de saúde: só é gravada com a concordância do colaborador
 * (`dietaryConsent`), que fica registrada com quem e quando.
 */
export const personPreferencesInput = z
  .object({
    shirtSize: z
      .enum(SHIRT_SIZES)
      .or(z.literal('').transform(() => null))
      .nullable()
      .optional(),
    shoeSize: z.coerce
      .number()
      .int()
      .min(SHOE_SIZE_MIN)
      .max(SHOE_SIZE_MAX)
      .or(z.literal('').transform(() => null))
      .nullable()
      .optional(),
    dietary: z.array(z.enum(DIETARY_RESTRICTIONS)).max(DIETARY_RESTRICTIONS.length).default([]),
    dietaryOther: optionalText(200),
    dietaryConsent: z.boolean().default(false),
  })
  .refine((v) => v.dietaryConsent || (v.dietary.length === 0 && !v.dietaryOther), {
    path: ['dietaryConsent'],
    message: 'Confirme que o colaborador concordou em informar as restrições alimentares',
  });
export type PersonPreferencesInput = z.infer<typeof personPreferencesInput>;

/** Meu cadastro (ADR 0083): a própria pessoa informa tamanhos e restrições (a concordância é dela). */
export const myPreferencesInput = z.object({
  shirtSize: z
    .enum(SHIRT_SIZES)
    .or(z.literal('').transform(() => null))
    .nullable()
    .optional(),
  shoeSize: z.coerce
    .number()
    .int()
    .min(SHOE_SIZE_MIN)
    .max(SHOE_SIZE_MAX)
    .or(z.literal('').transform(() => null))
    .nullable()
    .optional(),
  dietary: z.array(z.enum(DIETARY_RESTRICTIONS)).max(DIETARY_RESTRICTIONS.length).default([]),
  dietaryOther: optionalText(200),
});

/** Dar acesso em lote (ADR 0086): as pessoas escolhidas na prévia. */
export const accessGrantInput = z.object({
  personIds: z.array(z.uuid()).min(1).max(500),
});

/** Pedido de correção ao DP: o que está errado, em texto livre. */
/** Tipos de solicitação ao RH/DP (ADR 0091): a correção do cadastro é um deles (ADR 0083). */
export const HR_REQUEST_KINDS = ['correction', 'declaration', 'question', 'other'] as const;
export type HrRequestKind = (typeof HR_REQUEST_KINDS)[number];
export const HR_REQUEST_STATUSES = ['open', 'in_progress', 'resolved', 'cancelled'] as const;
export type HrRequestStatus = (typeof HR_REQUEST_STATUSES)[number];

/** Item medido pelo SLA corporativo e a atividade do catálogo (cadastrada pela tela do SLA). */
export const HR_REQUEST_SLA_RECORD_TYPE = 'people.hr_request';
export const HR_REQUEST_SLA_ACTIVITY = 'RH.SOLICITACOES.ATENDIMENTO';

export const correctionRequestInput = z.object({
  kind: z.enum(HR_REQUEST_KINDS).default('correction'),
  message: z.string().trim().min(3, 'Escreva o que precisa').max(1000),
});

/** Resposta de quem atende: obrigatória ao resolver ou cancelar. */
export const hrRequestAnswerInput = z.object({
  response: z.string().trim().min(1, 'Escreva a resposta').max(4000),
});

/** Lista de tamanhos e restrições em planilha: empresa da tela e colunas já traduzidas. */
export const preferencesExportInput = z.object({
  legalEntityId: z.uuid().nullable().optional(),
  /** Nome de cada restrição no idioma da tela. */
  dietaryLabels: z.partialRecord(z.enum(DIETARY_RESTRICTIONS), z.string().max(60)).default({}),
  columns: z
    .array(z.object({ key: z.string().regex(/^[a-zA-Z0-9_]{1,40}$/), label: z.string().max(80) }))
    .min(1)
    .max(20),
});

/** Foto da pessoa (ADR 0069): tipos aceitos e tamanho máximo do arquivo enviado. */
export const PERSON_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export const PERSON_PHOTO_MAX_BYTES = 2 * 1024 * 1024;
/** A tela reduz a foto antes de enviar: lado maior em pixels. */
export const PERSON_PHOTO_SIDE = 512;

/** Custo da folha da ADP (ADR 0091): consulta por ano. */
export const payrollCostQuery = z.object({
  year: z.coerce.number().int().min(2000).max(2100),
});

/** Ciência de políticas e treinamentos (ADR 0091): o que se registra e o cadastro do RH. */
export const ACKNOWLEDGEMENT_KINDS = ['policy', 'training'] as const;
export type AcknowledgementKind = (typeof ACKNOWLEDGEMENT_KINDS)[number];

export const acknowledgeInput = z.object({
  kind: z.enum(ACKNOWLEDGEMENT_KINDS),
  subjectId: z.uuid(),
});

export const trainingInput = z.object({
  legalEntityId: z.uuid().nullable().default(null),
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9][A-Z0-9._-]{0,39}$/, 'Use letras, números, ponto, hífen ou sublinhado'),
  name: z.object({
    'pt-BR': z.string().trim().min(1).max(200),
    en: z.string().trim().min(1).max(200),
  }),
  description: z.string().trim().max(2000).nullable().default(null),
  materialUrl: z
    .string()
    .trim()
    .max(500)
    .regex(/^(https:\/\/|\/)\S+$/, 'Endereço https:// ou caminho do sistema')
    .nullable()
    .default(null),
  validityMonths: z.number().int().min(1).max(120).nullable().default(null),
  isActive: z.boolean().default(true),
});
export type TrainingInput = z.infer<typeof trainingInput>;

/** Requisição de vaga (ADR 0091): o recrutamento é na ADP; aqui só o pedido e a aprovação. */
export const POSITION_REQUEST_STATUSES = [
  'draft',
  'submitted',
  'approved',
  'rejected',
  'cancelled',
  'filled',
] as const;
export type PositionRequestStatus = (typeof POSITION_REQUEST_STATUSES)[number];

const decimalText = z
  .string()
  .trim()
  .regex(/^\d{1,15}(\.\d{1,4})?$/, 'Valor com até 4 casas decimais');

export const positionRequestInput = z
  .object({
    legalEntityId: z.uuid(),
    branchId: z.uuid().nullable().default(null),
    costCenterId: z.uuid(),
    jobId: z.uuid(),
    quantity: z.number().int().min(1).max(99).default(1),
    reason: z.enum(['new', 'replacement']),
    replacedEmployeeId: z.uuid().nullable().default(null),
    expectedStart: z.iso.date().nullable().default(null),
    justification: z.string().trim().min(3, 'Explique a necessidade da vaga').max(2000),
    fpaPlanLineId: z.uuid().nullable().default(null),
    salary: z
      .object({ currency: z.string().length(3), min: decimalText, max: decimalText })
      .nullable()
      .default(null),
  })
  .refine((v) => (v.reason === 'replacement') === (v.replacedEmployeeId != null), {
    message: 'Na substituição, informe quem sai (e só nela)',
    path: ['replacedEmployeeId'],
  });
export type PositionRequestInput = z.infer<typeof positionRequestInput>;

export const positionFillInput = z.object({
  note: z.string().trim().min(1, 'Informe como a vaga foi preenchida').max(500),
});
